/**
 * L'accesso per codice, dalla parte del televisore.
 *
 * L'app non chiede più utente e password: mostra un codice, un rivenditore lo scansiona dal proprio
 * pannello, e le credenziali arrivano qui da sole. Il contratto sta in
 * `docs/project-design/qr-auth/SPEC.md` del monorepo.
 *
 * Qui c'è solo la parte che si può sbagliare senza che si veda, e che quindi va provata senza un
 * televisore: **quanto manca**, **cosa fare dopo ogni interrogazione**, e la differenza fra i
 * quattro esiti che dal divano sembrano tutti «non succede niente».
 */

/** Ogni quanto si interroga il servizio, in millisecondi. */
export const POLL_INTERVAL_MS = 5_000;

/**
 * Quanto si aspetta quando il servizio dice «troppe richieste» e non dice per quanto.
 *
 * Il doppio dell'intervallo normale, non di più: il codice vive novanta secondi in tutto, e un
 * arretramento generoso consumerebbe la finestra per proteggere un servizio che sta già dicendo
 * di stare bene abbastanza da rispondere.
 */
export const THROTTLED_BACKOFF_MS = 2 * POLL_INTERVAL_MS;

/** Cosa ha risposto il servizio a un'interrogazione. */
export type PollOutcome =
  /** `204` — richiesta aperta, nessun deposito ancora. */
  | { readonly kind: 'waiting' }
  /** `200` — credenziali pronte. Il servizio le ha già cancellate consegnandole. */
  | { readonly kind: 'ready'; readonly host: string; readonly token: string }
  /** `410` — scaduta o già consegnata. Serve un codice **nuovo**, non un altro tentativo. */
  | { readonly kind: 'gone' }
  /** `429` — troppe richieste. Non è colpa di chi guarda, e non si mostra come errore. */
  | { readonly kind: 'throttled'; readonly retryAfterMs: number | null }
  /** Nessuna risposta: rete, timeout, gateway muto. Il conto alla rovescia continua. */
  | { readonly kind: 'offline' };

/** Cosa fa la schermata dopo un'interrogazione. */
export type PairingStep =
  | 'keep-polling'
  /** Credenziali in mano: si memorizzano e si entra. */
  | 'enter'
  /** Il codice è morto: se ne chiede uno nuovo, **senza** ritentare quello vecchio. */
  | 'renew';

export interface PairingReaction {
  readonly step: PairingStep;
  /** Fra quanto si interroga di nuovo. Ignorato quando il passo non è `keep-polling`. */
  readonly delayMs: number;
}

/**
 * La regola che tiene insieme i quattro esiti.
 *
 * Il punto non è il codice: è che **`gone` e `waiting` non si confondano**. Se una richiesta morta
 * producesse un'altra interrogazione, il televisore resterebbe a chiedere per sempre un codice che
 * non esiste mostrando un conto alla rovescia fermo a zero — e da tre metri di distanza è
 * indistinguibile da un servizio che non risponde.
 *
 * E `offline` non è `gone`: il Wi-Fi che singhiozza non deve buttare via un codice ancora buono e
 * far ricominciare il rivenditore.
 */
export function reactToPoll(outcome: PollOutcome): PairingReaction {
  switch (outcome.kind) {
    case 'ready':
      return { step: 'enter', delayMs: 0 };
    case 'gone':
      return { step: 'renew', delayMs: 0 };
    case 'throttled':
      return {
        step: 'keep-polling',
        // Il servizio sa meglio di noi quanto deve aspettare: se lo dice, lo si ascolta.
        delayMs: outcome.retryAfterMs !== null && outcome.retryAfterMs > 0
          ? outcome.retryAfterMs
          : THROTTLED_BACKOFF_MS,
      };
    case 'waiting':
    case 'offline':
    default:
      return { step: 'keep-polling', delayMs: POLL_INTERVAL_MS };
  }
}

/**
 * Quando scade una richiesta, dato quel che ha detto il server.
 *
 * `expiresInSeconds` viene **dal servizio**, non dall'orologio dell'apparecchio: i televisori hanno
 * l'ora sbagliata, spesso di anni, e un conto alla rovescia calcolato su una data locale mostra
 * «ancora 40 secondi» su una richiesta già morta — oppure zero su una appena nata.
 */
export function pairingExpiryMs(expiresInSeconds: number, nowMs: number): number {
  const seconds = Number.isFinite(expiresInSeconds) && expiresInSeconds > 0 ? expiresInSeconds : 0;
  return nowMs + seconds * 1000;
}

/**
 * Quanti secondi restano, arrotondati per eccesso e mai negativi.
 *
 * Per eccesso perché un conto che mostra `0` mentre la richiesta è ancora buona invita a premere
 * «richiedi un codice nuovo» un istante prima che il rivenditore finisca di scansionare.
 */
export function pairingRemainingSeconds(expiresAtMs: number, nowMs: number): number {
  const remaining = Math.ceil((expiresAtMs - nowMs) / 1000);
  return remaining > 0 ? remaining : 0;
}

/** Se il codice è ancora vivo. A zero **non si interroga più**: consuma il limite per niente. */
export function pairingIsAlive(expiresAtMs: number, nowMs: number): boolean {
  return pairingRemainingSeconds(expiresAtMs, nowMs) > 0;
}

/**
 * Il codice come si legge da tre metri: due gruppi di quattro.
 *
 * `k7m2x9qp` diventa `k7m2 x9qp`. Otto caratteri di fila si sbagliano a dettare e a digitare col
 * telecomando; a gruppi di quattro no, ed è la stessa ragione per cui i numeri di carta si scrivono
 * a gruppi. Lo spazio è solo grafico: il codice vero resta di otto caratteri.
 */
export function formatPairingCode(code: string): string {
  if (code.length !== 8) return code;
  return `${code.slice(0, 4)} ${code.slice(4)}`;
}
